import { createStore, type Action, createActionsHook, createStateHook } from 'react-sweet-state';

import { getLogger } from '@confluence/logger';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

const logger = getLogger('useLoomAiMeetingNotesSideNavCardListener');

interface State {
	/**
	 * `true` if the user has performed an action making them eligible to see the side nav card.
	 *
	 * **Note**: This does not account for eligibility checks other than the immediate user interaction
	 * and experiment checks, those are all handled inside post-office in the message template.
	 */
	isSideNavCardEligible: boolean;
}

type Actions = {
	/**
	 * Will set a state notifying the relevant consumers that the user has clicked on a meeting notes template
	 * @param templateId the id of the template to check is a meeting note template
	 *
	 * This action has no side effects otherwise (that is to be handled by a consumer)
	 */
	showLoomAiMeetingNotesSideNavCardIfMeetingNotesTemplate: (
		templateId: string,
		onLocalStorageFail?: () => void,
	) => Action<State, void, void>;
};

const ACCEPTED_MEETING_NOTES_TEMPLATE_IDS = [
	'com.atlassian.confluence.plugins.confluence-business-blueprints:1-on-1-meeting-blueprint',
	'com.atlassian.confluence.plugins.confluence-business-blueprints:all-hands-meeting-blueprint',
	'com.atlassian.confluence.plugins.confluence-business-blueprints:meeting-notes-blueprint',
	'com.atlassian.confluence.plugins.confluence-business-blueprints:page-led-meetings-blueprint',
	'com.atlassian.confluence.plugins.confluence-business-blueprints:remote-team-meeting-blueprint',
	'com.atlassian.confluence.plugins.confluence-business-blueprints:sprint-planning-meeting-blueprint',
	'com.atlassian.confluence.plugins.confluence-business-blueprints:recurring-meeting-minutes-blueprint',
] as const;

type AcceptedTemplateId = (typeof ACCEPTED_MEETING_NOTES_TEMPLATE_IDS)[number];

const isAcceptedTemplateId = (templateId: string): templateId is AcceptedTemplateId => {
	return (ACCEPTED_MEETING_NOTES_TEMPLATE_IDS as readonly string[]).includes(templateId);
};

const store = createStore<State, Actions>({
	initialState: {
		isSideNavCardEligible: false,
	},
	actions: {
		showLoomAiMeetingNotesSideNavCardIfMeetingNotesTemplate:
			(templateId, onLocalStorageFail) =>
			({ setState }) => {
				if (isAcceptedTemplateId(templateId)) {
					try {
						/**
						 * Reason we are using local storage:
						 * - it persists across browser sessions and tab closures, ensuring the user's interaction is remembered
						 * - This is important for the "create from template" macro which opens the template in a new tab (instead of same page)
						 */
						localStorage.setItem(keys.USER_HAS_CLICKED_ON_MEETING_NOTES_TEMPLATE, true);
					} catch (setError) {
						/**
						 * this is here to avoid uncaught storage errors
						 */
						logger.error`An error occurred while setting local storage: ${setError}`;
						onLocalStorageFail?.();
					}
					setState({ isSideNavCardEligible: true });
				}
			},
	},
});

export const useLoomAiMeetingNotesSideNavCardState = createStateHook(store);
export const useLoomAiMeetingNotesSideNavCardActions = createActionsHook(store);
